import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Container, Button, Grid } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BuildIcon from "@mui/icons-material/Build";
import PaymentIcon from "@mui/icons-material/Payment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PublicFooter from "examples/PublicFooter";
import SEO from "components/SEO";
import PublicNavbar from "components/PublicNavbar";
import { useAuth } from "layouts/authentication/auth/auth";

const BRAND_BLUE = "#0072D4";
const BRAND_ORANGE = "#F56242";
const NAVY = "#0f172a";

const features = [
  {
    Icon: HomeWorkIcon,
    title: "Emlak Yönetimi",
    desc: "Kiracılarınızı, sözleşmelerinizi ve mülklerinizi tek platformda yönetin.",
    color: BRAND_BLUE,
    bg: "rgba(0,114,212,0.08)",
  },
  {
    Icon: VerifiedUserIcon,
    title: "Garantili Kira",
    desc: "Kiracınız ödemezse veya evden çıkarsa, RENIX sorumluluğu üstlenir.",
    color: "#0d9488",
    bg: "rgba(13,148,136,0.08)",
  },
  {
    Icon: BuildIcon,
    title: "Teknik Destek",
    desc: "Dairenizle ilgili tüm teknik sorunlar için profesyonel ekiplerimiz hizmetinizde.",
    color: BRAND_ORANGE,
    bg: "rgba(245,98,66,0.08)",
  },
  {
    Icon: PaymentIcon,
    title: "Ödeme Yönetimi",
    desc: "Tüm ödemeleri tek yerden takip edin ve yönetin.",
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.08)",
  },
  {
    Icon: EventAvailableIcon,
    title: "Kısa Süreli Kiralama",
    desc: "Kısa dönem kiralama rezervasyonlarını sistemimiz üzerinden gerçekleştirin.",
    color: "#0284c7",
    bg: "rgba(2,132,199,0.08)",
  },
  {
    Icon: SupportAgentIcon,
    title: "7/24 Destek",
    desc: "Ev sahipleri için tam destek hizmeti ve iletişim yönetimi.",
    color: "#059669",
    bg: "rgba(5,150,105,0.08)",
  },
];

function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleTeknikVermek = () => {
    navigate("/sizi-arayalim?mode=teknik");
  };

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <SEO
        title="Renix - Mülk Yönetim Sistemi"
        description="Mülklerinizi yönetmenin en kolay yolu. Kiracılardan teknik sorunlara, ödemelerden sözleşmelere kadar her şeyi tek platformda yönetin."
        canonical="https://renix.app"
      />
      <PublicNavbar />

      {/* ─── HERO ─── */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(135deg, ${NAVY} 0%, #0c2a5e 50%, ${NAVY} 100%)`,
          minHeight: { xs: "auto", md: "88vh" },
          display: "flex",
          alignItems: "center",
          py: { xs: 10, md: 0 },
        }}
      >
        {/* Decorative background circles */}
        <Box
          sx={{
            position: "absolute",
            width: 800,
            height: 800,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND_BLUE}22 0%, transparent 70%)`,
            top: -300,
            right: -200,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND_ORANGE}18 0%, transparent 70%)`,
            bottom: -200,
            left: -100,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `radial-gradient(${BRAND_BLUE}14 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
            pointerEvents: "none",
            opacity: 0.6,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            {/* Left: text content */}
            <Grid item xs={12} md={6} lg={6}>
              {/* Eyebrow badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 10,
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  mb: 3,
                }}
              >
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: BRAND_ORANGE }} />
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                  }}
                >
                  Türkiye&apos;nin Akıllı Mülk Platformu
                </Typography>
              </Box>

              {/* Main headline */}
              <Typography
                sx={{
                  color: "#ffffff",
                  fontWeight: 900,
                  fontSize: { xs: "2.4rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
                  lineHeight: 1.1,
                  letterSpacing: "-2px",
                  mb: 3,
                }}
              >
                Mülk yönetimi,
                <br />
                <Box component="span" sx={{ color: BRAND_ORANGE }}>
                  artık kolay.
                </Box>
              </Typography>

              {/* Subtitle */}
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mb: 5,
                  maxWidth: 480,
                }}
              >
                Kiracılardan teknik sorunlara, ödemelerden sözleşmelere kadar her şeyi tek
                platformda yönetin.
              </Typography>

              {/* CTA Buttons */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Button
                  onClick={() => navigate("/authentication/sign-in")}
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: BRAND_BLUE,
                    color: "#ffffff",
                    px: 3.5,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    boxShadow: `0 8px 24px ${BRAND_BLUE}44`,
                    transition: "all 0.25s",
                    "&:hover": {
                      bgcolor: "#005eb3",
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 32px ${BRAND_BLUE}55`,
                    },
                  }}
                >
                  Sisteme Giriş Yap
                </Button>
                <Button
                  onClick={() => navigate("/ilanlar")}
                  variant="outlined"
                  sx={{
                    borderColor: BRAND_ORANGE,
                    color: BRAND_ORANGE,
                    px: 3.5,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    borderWidth: 2,
                    transition: "all 0.25s",
                    "&:hover": {
                      borderColor: BRAND_ORANGE,
                      bgcolor: "rgba(245,98,66,0.1)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  İlanları Görüntüle
                </Button>
                <Button
                  onClick={() => (window.location.href = "/sizi-arayalim")}
                  variant="text"
                  sx={{
                    color: "rgba(255,255,255,0.55)",
                    px: 2,
                    py: 1.5,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    transition: "color 0.2s",
                    "&:hover": { color: "rgba(255,255,255,0.9)", bgcolor: "transparent" },
                  }}
                >
                  İletişime Geç →
                </Button>
              </Box>
            </Grid>

            {/* Right: logo visual */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box sx={{ position: "relative" }}>
                {/* Glow ring */}
                <Box
                  sx={{
                    width: { md: 320, lg: 380 },
                    height: { md: 320, lg: 380 },
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${BRAND_BLUE}33 0%, ${BRAND_BLUE}11 50%, transparent 70%)`,
                    border: `1px solid ${BRAND_BLUE}44`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 0 80px ${BRAND_BLUE}33, inset 0 0 60px ${BRAND_BLUE}18`,
                  }}
                >
                  <Box
                    component="img"
                    src={require("assets/images/renix-logo-white-transparent.png")}
                    alt="Renix"
                    sx={{
                      width: { md: 160, lg: 200 },
                      filter: `drop-shadow(0 0 30px ${BRAND_BLUE}88)`,
                    }}
                  />
                </Box>
                {/* Floating service title badges */}
                {[
                  { label: "Mülk Yönetimi", dot: BRAND_BLUE, top: "5%", right: "-10%" },
                  { label: "Emlak Hizmetleri", dot: "#0d9488", bottom: "20%", left: "-15%" },
                  { label: "Teknik Destek", dot: BRAND_ORANGE, top: "58%", right: "-15%" },
                  { label: "7/24 Destek", dot: "#7c3aed", top: "22%", left: "-13%" },
                ].map((badge) => (
                  <Box
                    key={badge.label}
                    sx={{
                      position: "absolute",
                      top: badge.top,
                      bottom: badge.bottom,
                      left: badge.left,
                      right: badge.right,
                      bgcolor: "rgba(255,255,255,0.1)",
                      backdropFilter: "blur(12px)",
                      border: "1px solid rgba(255,255,255,0.22)",
                      borderRadius: 10,
                      px: 2,
                      py: 0.875,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: badge.dot,
                        flexShrink: 0,
                      }}
                    />
                    <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: "0.8rem" }}>
                      {badge.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── FEATURES ─── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "white" }}>
        <Container maxWidth="lg">
          {/* Section header */}
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: 10,
                bgcolor: `${BRAND_BLUE}12`,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  color: BRAND_BLUE,
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}
              >
                Hizmetlerimiz
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.8rem", md: "2.5rem" },
                color: NAVY,
                letterSpacing: "-1px",
                mb: 2,
              }}
            >
              Neden Renix?
            </Typography>
            <Typography
              sx={{
                color: "#64748b",
                fontSize: "1rem",
                maxWidth: 500,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              Mülk yönetiminin her aşamasında yanınızdayız.
            </Typography>
          </Box>

          {/* Feature cards grid */}
          <Grid container spacing={3}>
            {features.map((feature) => (
              <Grid item xs={12} sm={6} lg={4} key={feature.title}>
                <Box
                  sx={{
                    p: 3.5,
                    borderRadius: 3,
                    border: "1px solid #f1f5f9",
                    bgcolor: "white",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "default",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                      borderColor: feature.color + "44",
                    },
                  }}
                >
                  {/* Icon box */}
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      bgcolor: feature.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.5,
                    }}
                  >
                    <feature.Icon sx={{ fontSize: 26, color: feature.color }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: NAVY, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.875rem", lineHeight: 1.7 }}>
                    {feature.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── TEKNİK HİZMETLER SECTION ─── */}
      <Box sx={{ bgcolor: "#ffffff", py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 999,
                bgcolor: `${BRAND_ORANGE}12`,
                mb: 2,
              }}
            >
              <BuildIcon sx={{ fontSize: 18, color: BRAND_ORANGE }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  letterSpacing: "0.06em",
                  color: BRAND_ORANGE,
                  textTransform: "uppercase",
                }}
              >
                Teknik Hizmetler
              </Typography>
            </Box>
            <Typography
              sx={{ fontWeight: 800, fontSize: { xs: "1.6rem", md: "2rem" }, color: NAVY }}
            >
              Teknik hizmet mi vermek istiyorsunuz?
            </Typography>
            <Typography
              sx={{
                color: "#64748b",
                fontSize: "1.05rem",
                lineHeight: 1.7,
                mt: 2,
                maxWidth: 560,
                mx: "auto",
              }}
            >
              Uzmanlık alanınızı paylaşın, hizmet sağlayıcı ağımıza katılın.
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleTeknikVermek}
              sx={{
                mt: 4,
                bgcolor: BRAND_ORANGE,
                color: "#ffffff",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1rem",
                borderRadius: "12px",
                px: 4,
                py: 1.25,
                boxShadow: `0 8px 24px ${BRAND_ORANGE}44`,
                "&:hover": { bgcolor: BRAND_ORANGE, filter: "brightness(0.95)" },
              }}
            >
              Başvur
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ─── CTA SECTION ─── */}
      <Box
        sx={{
          bgcolor: "#f0f7ff",
          borderTop: `4px solid ${BRAND_BLUE}22`,
          py: { xs: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle decorative dot pattern */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `radial-gradient(${BRAND_BLUE}18 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
            pointerEvents: "none",
            opacity: 0.5,
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          {/* Eyebrow */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.5,
              borderRadius: 10,
              bgcolor: `${BRAND_BLUE}12`,
              mb: 2.5,
            }}
          >
            <Typography
              sx={{
                color: BRAND_BLUE,
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              İletişim
            </Typography>
          </Box>
          <Typography
            sx={{
              color: NAVY,
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.5rem" },
              letterSpacing: "-1px",
              mb: 2,
            }}
          >
            Hemen Başlayın
          </Typography>
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "1rem",
              mb: 5,
              maxWidth: 480,
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Kullanıcı kayıtları Renix tarafından yapılmaktadır. Sisteme dahil olmak için bizimle
            iletişime geçin.
          </Typography>

          {/* Contact cards */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", mb: 5 }}>
            {[
              { icon: EmailIcon, label: "E-posta", value: "info@renix.app" },
              { icon: PhoneIcon, label: "Telefon", value: "+90 533 413 2071" },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 3,
                  py: 1.75,
                  borderRadius: 2.5,
                  bgcolor: "white",
                  border: `1px solid ${BRAND_BLUE}22`,
                  boxShadow: "0 2px 12px rgba(0,114,212,0.08)",
                  minWidth: 220,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 1.5,
                    bgcolor: `${BRAND_BLUE}12`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <item.icon sx={{ fontSize: 20, color: BRAND_BLUE }} />
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      color: "#94a3b8",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: NAVY, fontWeight: 700, fontSize: "0.9rem" }}>
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Button
            onClick={() => (window.location.href = "/sizi-arayalim")}
            variant="contained"
            size="large"
            sx={{
              bgcolor: BRAND_ORANGE,
              color: "#ffffff",
              px: 5,
              py: 1.75,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              fontSize: "1rem",
              boxShadow: `0 8px 24px ${BRAND_ORANGE}44`,
              transition: "all 0.25s",
              "&:hover": {
                bgcolor: "#e04f34",
                transform: "translateY(-2px)",
                boxShadow: `0 12px 32px ${BRAND_ORANGE}55`,
              },
            }}
          >
            Bize Ulaşın
          </Button>
        </Container>
      </Box>

      <PublicFooter />
    </Box>
  );
}

export default LandingPage;
